import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import {
  Plus, Search, ChevronDown, ChevronUp, Trash2, Edit3, Save, X,
  Printer, Mail, FileText, Package, Leaf, ClipboardList
} from 'lucide-react';

const emptyConsultation = {
  clientId: '', clientName: '', date: new Date().toISOString().split('T')[0],
  consultationNotes: '', skinAssessment: '',
  treatmentPlan: [], skincareRecs: [], supplementRecs: [],
  quoteSubtotal: 0, quoteDiscount: 0, quoteTotal: 0, quoteNotes: '',
};

export default function Consultations() {
  const [consultations, setConsultations] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyConsultation);
  const [activeTab, setActiveTab] = useState('plan');
  const printRef = useRef();

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [c, cl, pr, tr, su] = await Promise.all([
      supabase.from('consultations').select('*').order('created_at', { ascending: false }),
      supabase.from('clients').select('id, "firstName", "lastName", dob, "address1", "address2", town, county, postcode, email').order('"lastName"'),
      supabase.from('products').select('id, name, brand, supplier, rrp, "howToUse", "whenToUse"').eq('status', 'Active').order('name'),
      supabase.from('treatments').select('id, name, price, category').order('name'),
      supabase.from('supplements').select('id, name, code, dosage, price').order('name'),
    ]);
    setConsultations(c.data || []);
    setClients(cl.data || []);
    setProducts(pr.data || []);
    setTreatments(tr.data || []);
    setSupplements(su.data || []);
  };

  const clientName = (c) => `${c.firstName || ''} ${c.lastName || ''}`.trim();
  const clientAddress = (c) => [c.address1, c.address2, c.town, c.county, c.postcode].filter(Boolean).join(', ');

  const recalcQuote = (f) => {
    const treatmentTotal = (f.treatmentPlan || []).reduce((s, t) => s + (parseFloat(t.price) || 0) * (parseInt(t.sessions) || 1), 0);
    const skincareTotal = (f.skincareRecs || []).reduce((s, p) => s + (parseFloat(p.rrp) || 0) * (parseInt(p.qty) || 1), 0);
    const subtotal = treatmentTotal + skincareTotal;
    const discountAmt = subtotal * ((parseFloat(f.quoteDiscount) || 0) / 100);
    return { ...f, quoteSubtotal: subtotal, quoteTotal: subtotal - discountAmt };
  };

  const setFormRecalc = (updates) => setForm(prev => recalcQuote({ ...prev, ...updates }));
  const setEditingRecalc = (updates) => setEditing(prev => recalcQuote({ ...prev, ...updates }));

  const saveNew = async () => {
    if (!form.clientId) return alert('Please select a client');
    const { id: _id, created_at, updated_at, ...rest } = form;
    await supabase.from('consultations').insert([rest]);
    setForm(emptyConsultation); setShowNew(false); load();
  };

  const saveEdit = async () => {
    const { id, created_at, ...rest } = editing;
    await supabase.from('consultations').update({ ...rest, updated_at: new Date().toISOString() }).eq('id', id);
    setEditing(null); load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this consultation?')) return;
    await supabase.from('consultations').delete().eq('id', id);
    setExpanded(null); load();
  };

  // ── Treatment plan helpers ──────────────────────────────────────────────
  const addTreatment = (f, setF, t) => {
    const plan = [...(f.treatmentPlan || []), { id: t.id, name: t.name, price: t.price || '', sessions: 1, notes: '' }];
    setF({ treatmentPlan: plan });
  };
  const removeTreatment = (f, setF, idx) => {
    const plan = (f.treatmentPlan || []).filter((_, i) => i !== idx);
    setF({ treatmentPlan: plan });
  };
  const updateTreatment = (f, setF, idx, key, val) => {
    const plan = (f.treatmentPlan || []).map((t, i) => i === idx ? { ...t, [key]: val } : t);
    setF({ treatmentPlan: plan });
  };

  // ── Skincare rec helpers ────────────────────────────────────────────────
  const addProduct = (f, setF, p) => {
    if ((f.skincareRecs || []).find(r => r.id === p.id)) return;
    const recs = [...(f.skincareRecs || []), { id: p.id, name: p.name, brand: p.brand, supplier: p.supplier, rrp: p.rrp || '', qty: 1, howToUse: p.howToUse || '', whenToUse: p.whenToUse || '', notes: '' }];
    setF({ skincareRecs: recs });
  };
  const removeProduct = (f, setF, idx) => {
    const recs = (f.skincareRecs || []).filter((_, i) => i !== idx);
    setF({ skincareRecs: recs });
  };
  const updateProduct = (f, setF, idx, key, val) => {
    const recs = (f.skincareRecs || []).map((r, i) => i === idx ? { ...r, [key]: val } : r);
    setF({ skincareRecs: recs });
  };

  // ── Supplement rec helpers ──────────────────────────────────────────────
  const addSupplement = (f, setF, s) => {
    if ((f.supplementRecs || []).find(r => r.id === s.id)) return;
    const recs = [...(f.supplementRecs || []), { id: s.id, name: s.name, dosage: s.dosage || '', notes: '' }];
    setF({ supplementRecs: recs });
  };
  const removeSupplement = (f, setF, idx) => {
    const recs = (f.supplementRecs || []).filter((_, i) => i !== idx);
    setF({ supplementRecs: recs });
  };

  // ── Print ───────────────────────────────────────────────────────────────
  const printConsultation = (con, tab) => {
    const client = clients.find(c => c.id === con.clientId) || {};
    const w = window.open('', '_blank');
    const sections = {
      plan: buildPrintPlan(con, client),
      skincare: buildPrintSkincare(con, client),
      order: buildPrintOrder(con, client),
      all: buildPrintAll(con, client),
    };
    w.document.write(printWrapper(sections[tab] || sections.all));
    w.document.close();
    w.print();
  };

  const fmt = (n) => `£${parseFloat(n || 0).toFixed(2)}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';

  const printWrapper = (body) => `<!DOCTYPE html><html><head><title>Dorset Aesthetics</title>
<style>
  body { font-family: Georgia, serif; color: #1a1a18; max-width: 700px; margin: 40px auto; font-size: 13px; line-height: 1.6; }
  h1 { font-size: 22px; margin-bottom: 2px; } h2 { font-size: 15px; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  .clinic { font-size: 11px; color: #666; margin-bottom: 20px; }
  .client-block { background: #f9f7f4; border: 1px solid #ddd; padding: 12px 16px; margin-bottom: 20px; border-radius: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #666; border-bottom: 1px solid #ccc; padding: 4px 8px; }
  td { padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
  .total-row td { font-weight: bold; border-top: 2px solid #333; }
  .note { font-size: 11px; color: #666; margin-top: 4px; }
  .sig-block { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 16px; font-size: 12px; }
  @media print { body { margin: 20px; } }
</style></head><body>${body}</body></html>`;

  const clientBlock = (con, client) => `
    <div class="client-block">
      <strong>${con.clientName}</strong><br/>
      ${client.dob ? `DOB: ${fmtDate(client.dob)}<br/>` : ''}
      ${clientAddress(client) ? `${clientAddress(client)}<br/>` : ''}
      ${client.email ? client.email : ''}
    </div>`;

  const buildPrintPlan = (con, client) => `
    <h1>Treatment Plan</h1>
    <div class="clinic">Dorset Aesthetics &nbsp;·&nbsp; ${fmtDate(con.date)}</div>
    ${clientBlock(con, client)}
    ${con.consultationNotes ? `<h2>Consultation Notes</h2><p>${con.consultationNotes}</p>` : ''}
    ${con.skinAssessment ? `<h2>Skin Assessment</h2><p>${con.skinAssessment}</p>` : ''}
    ${(con.treatmentPlan || []).length > 0 ? `
      <h2>Recommended Treatments</h2>
      <table><thead><tr><th>Treatment</th><th>Sessions</th><th>Price</th><th>Notes</th></tr></thead><tbody>
      ${(con.treatmentPlan || []).map(t => `<tr><td>${t.name}</td><td>${t.sessions || 1}</td><td>${fmt(t.price)}</td><td>${t.notes || ''}</td></tr>`).join('')}
      </tbody></table>` : ''}
    ${buildQuoteSection(con)}
    <div class="sig-block">Practitioner: Dorset Aesthetics &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Signature: _____________________ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date: _________</div>`;

  const buildPrintSkincare = (con, client) => `
    <h1>Skincare Recommendations</h1>
    <div class="clinic">Dorset Aesthetics &nbsp;·&nbsp; ${fmtDate(con.date)}</div>
    ${clientBlock(con, client)}
    ${(con.skincareRecs || []).length > 0 ? `
      <table><thead><tr><th>Product</th><th>When to Use</th><th>How to Use</th><th>Price</th></tr></thead><tbody>
      ${(con.skincareRecs || []).map(p => `
        <tr><td><strong>${p.brand || ''} ${p.name}</strong>${p.notes ? `<div class="note">${p.notes}</div>` : ''}</td>
        <td>${p.whenToUse || ''}</td><td>${p.howToUse || ''}</td><td>${fmt(p.rrp)}</td></tr>`).join('')}
      </tbody></table>
      ${buildQuoteSection(con)}` : '<p>No skincare recommendations recorded.</p>'}`;

  const buildPrintOrder = (con, client) => {
    const bySupplier = {};
    (con.skincareRecs || []).forEach(p => {
      const sup = p.supplier || 'Unknown Supplier';
      if (!bySupplier[sup]) bySupplier[sup] = [];
      bySupplier[sup].push(p);
    });
    return `
    <h1>Product Order</h1>
    <div class="clinic">Dorset Aesthetics &nbsp;·&nbsp; ${fmtDate(con.date)} &nbsp;·&nbsp; For: ${con.clientName}</div>
    ${Object.entries(bySupplier).map(([sup, prods]) => `
      <h2>${sup}</h2>
      <table><thead><tr><th>Product</th><th>Qty</th></tr></thead><tbody>
      ${prods.map(p => `<tr><td>${p.brand || ''} ${p.name}</td><td>${p.qty || 1}</td></tr>`).join('')}
      </tbody></table>`).join('')}
    <div class="sig-block" style="margin-top:30px">Please supply the above for client: <strong>${con.clientName}</strong><br/>Many thanks, Dorset Aesthetics</div>`;
  };

  const buildPrintAll = (con, client) => buildPrintPlan(con, client) + '<hr/>' + buildPrintSkincare(con, client);

  const buildQuoteSection = (con) => {
    if (!con.quoteSubtotal) return '';
    const disc = parseFloat(con.quoteDiscount) || 0;
    return `
      <h2>Quote</h2>
      <table><tbody>
        <tr><td>Subtotal</td><td style="text-align:right">${fmt(con.quoteSubtotal)}</td></tr>
        ${disc > 0 ? `<tr><td>Discount (${disc}%)</td><td style="text-align:right">-${fmt(con.quoteSubtotal * disc / 100)}</td></tr>` : ''}
        <tr class="total-row"><td>Total</td><td style="text-align:right">${fmt(con.quoteTotal)}</td></tr>
      </tbody></table>
      ${con.quoteNotes ? `<p class="note" style="margin-top:8px">${con.quoteNotes}</p>` : ''}`;
  };

  // ── Email supplier order ────────────────────────────────────────────────
  const emailOrder = (con) => {
    const bySupplier = {};
    (con.skincareRecs || []).forEach(p => {
      const sup = p.supplier || 'Unknown';
      if (!bySupplier[sup]) bySupplier[sup] = [];
      bySupplier[sup].push(p);
    });
    const body = Object.entries(bySupplier).map(([sup, prods]) =>
      `${sup}:\n${prods.map(p => `  - ${p.brand || ''} ${p.name} x${p.qty || 1}`).join('\n')}`
    ).join('\n\n');
    window.open(`mailto:?subject=Product Order - ${con.clientName} - ${fmtDate(con.date)}&body=${encodeURIComponent(`Please supply the following for client ${con.clientName}:\n\n${body}\n\nMany thanks,\nDorset Aesthetics`)}`);
  };

  // ── Form component ──────────────────────────────────────────────────────
  const ConsultationForm = ({ data, setData, onSave, onCancel, isNew }) => {
    const setF = (updates) => typeof updates === 'function' ? setData(updates) : setData(prev => recalcQuote({ ...prev, ...updates }));
    const [productSearch, setProductSearch] = useState('');
    const [treatmentSearch, setTreatmentSearch] = useState('');
    const [suppSearch, setSuppSearch] = useState('');

    return (
      <div className="patient-form">
        {/* Client & Date */}
        <div className="form-section-title">Consultation Details</div>
        <div className="form-grid">
          <div className="form-field">
            <label>Client *</label>
            <select value={data.clientId} onChange={e => {
              const c = clients.find(x => x.id === e.target.value);
              setF({ clientId: e.target.value, clientName: c ? clientName(c) : '' });
            }}>
              <option value="">Select client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Date</label>
            <input type="date" value={data.date} onChange={e => setF({ date: e.target.value })} />
          </div>
          <div className="form-field full">
            <label>Consultation Notes</label>
            <textarea rows={3} value={data.consultationNotes} onChange={e => setF({ consultationNotes: e.target.value })} placeholder="Presenting concerns, client goals, discussion..." />
          </div>
          <div className="form-field full">
            <label>Skin Assessment</label>
            <textarea rows={3} value={data.skinAssessment} onChange={e => setF({ skinAssessment: e.target.value })} placeholder="Skin condition observations, Fitzpatrick type, contraindications..." />
          </div>
        </div>

        {/* Tabs */}
        <div className="patient-tabs" style={{ marginTop: '1.5rem' }}>
          {[
            { id: 'plan', label: 'Treatment Plan', icon: ClipboardList },
            { id: 'skincare', label: 'Skincare', icon: Package },
            { id: 'supplements', label: 'Supplements', icon: Leaf },
            { id: 'quote', label: 'Quote', icon: FileText },
          ].map(tab => (
            <button key={tab.id} className={`patient-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}>
              <tab.icon size={12} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Treatment Plan Tab */}
        {activeTab === 'plan' && (
          <div style={{ marginTop: '1rem' }}>
            <div className="search-bar" style={{ marginBottom: '0.75rem' }}>
              <Search size={14} className="search-icon" />
              <input value={treatmentSearch} onChange={e => setTreatmentSearch(e.target.value)} placeholder="Search treatments to add..." className="search-input" />
            </div>
            {treatmentSearch && (
              <div className="picker-list">
                {treatments.filter(t => t.name.toLowerCase().includes(treatmentSearch.toLowerCase())).map(t => (
                  <button key={t.id} className="picker-item" onClick={() => { addTreatment(data, setF, t); setTreatmentSearch(''); }}>
                    <strong>{t.name}</strong> {t.price && <span className="soft">— £{t.price}</span>}
                  </button>
                ))}
              </div>
            )}
            {(data.treatmentPlan || []).length === 0 && !treatmentSearch && (
              <p className="log-empty">No treatments added yet. Search above to add.</p>
            )}
            {(data.treatmentPlan || []).map((t, i) => (
              <div key={i} className="rec-row">
                <div className="rec-main">
                  <span className="rec-name">{t.name}</span>
                  <div className="rec-fields">
                    <label>Sessions</label>
                    <input type="number" min="1" value={t.sessions} onChange={e => { updateTreatment(data, setF, i, 'sessions', e.target.value); }} style={{ width: '60px' }} />
                    <label>Price £</label>
                    <input type="number" step="0.01" value={t.price} onChange={e => { updateTreatment(data, setF, i, 'price', e.target.value); }} style={{ width: '80px' }} />
                    <label>Notes</label>
                    <input value={t.notes} onChange={e => updateTreatment(data, setF, i, 'notes', e.target.value)} style={{ flex: 1 }} />
                  </div>
                </div>
                <button className="icon-btn danger" onClick={() => removeTreatment(data, setF, i)}><X size={13} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Skincare Tab */}
        {activeTab === 'skincare' && (
          <div style={{ marginTop: '1rem' }}>
            <div className="search-bar" style={{ marginBottom: '0.75rem' }}>
              <Search size={14} className="search-icon" />
              <input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products to recommend..." className="search-input" />
            </div>
            {productSearch && (
              <div className="picker-list">
                {products.filter(p => `${p.name} ${p.brand}`.toLowerCase().includes(productSearch.toLowerCase())).map(p => (
                  <button key={p.id} className="picker-item" onClick={() => { addProduct(data, setF, p); setProductSearch(''); }}>
                    <strong>{p.brand} {p.name}</strong> {p.rrp && <span className="soft">— £{p.rrp}</span>}
                  </button>
                ))}
              </div>
            )}
            {(data.skincareRecs || []).length === 0 && !productSearch && (
              <p className="log-empty">No products added. Search above to add recommendations.</p>
            )}
            {(data.skincareRecs || []).map((p, i) => (
              <div key={i} className="rec-row">
                <div className="rec-main">
                  <span className="rec-name">{p.brand} {p.name}</span>
                  <div className="rec-fields">
                    <label>Qty</label>
                    <input type="number" min="1" value={p.qty} onChange={e => updateProduct(data, setF, i, 'qty', e.target.value)} style={{ width: '60px' }} />
                    <label>RRP £</label>
                    <input type="number" step="0.01" value={p.rrp} onChange={e => updateProduct(data, setF, i, 'rrp', e.target.value)} style={{ width: '80px' }} />
                    <label>When</label>
                    <input value={p.whenToUse} onChange={e => updateProduct(data, setF, i, 'whenToUse', e.target.value)} style={{ width: '90px' }} />
                  </div>
                  <div className="rec-fields" style={{ marginTop: '4px' }}>
                    <label>Personal note for client</label>
                    <input value={p.notes} onChange={e => updateProduct(data, setF, i, 'notes', e.target.value)} style={{ flex: 1 }} placeholder="e.g. Start every other day, build up..." />
                  </div>
                </div>
                <button className="icon-btn danger" onClick={() => removeProduct(data, setF, i)}><X size={13} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Supplements Tab */}
        {activeTab === 'supplements' && (
          <div style={{ marginTop: '1rem' }}>
            <div className="search-bar" style={{ marginBottom: '0.75rem' }}>
              <Search size={14} className="search-icon" />
              <input value={suppSearch} onChange={e => setSuppSearch(e.target.value)} placeholder="Search supplements..." className="search-input" />
            </div>
            {suppSearch && (
              <div className="picker-list">
                {supplements.filter(s => `${s.name} ${s.code}`.toLowerCase().includes(suppSearch.toLowerCase())).map(s => (
                  <button key={s.id} className="picker-item" onClick={() => { addSupplement(data, setF, s); setSuppSearch(''); }}>
                    <strong>{s.name}</strong> {s.code && <span className="soft">({s.code})</span>} {s.price && <span className="soft">— £{s.price}</span>}
                  </button>
                ))}
              </div>
            )}
            {(data.supplementRecs || []).length === 0 && !suppSearch && (
              <p className="log-empty">No supplements added. Search above.</p>
            )}
            {(data.supplementRecs || []).map((s, i) => (
              <div key={i} className="rec-row">
                <div className="rec-main">
                  <span className="rec-name">{s.name}</span>
                  <div className="rec-fields">
                    <label>Dosage</label>
                    <input value={s.dosage} onChange={e => {
                      const recs = (data.supplementRecs || []).map((r, j) => j === i ? { ...r, dosage: e.target.value } : r);
                      setF({ supplementRecs: recs });
                    }} style={{ flex: 1 }} />
                    <label>Notes</label>
                    <input value={s.notes} onChange={e => {
                      const recs = (data.supplementRecs || []).map((r, j) => j === i ? { ...r, notes: e.target.value } : r);
                      setF({ supplementRecs: recs });
                    }} style={{ flex: 1 }} />
                  </div>
                </div>
                <button className="icon-btn danger" onClick={() => removeSupplement(data, setF, i)}><X size={13} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Quote Tab */}
        {activeTab === 'quote' && (
          <div style={{ marginTop: '1rem' }}>
            <div className="quote-summary">
              <div className="quote-row"><span>Treatments subtotal</span>
                <span>{fmt((data.treatmentPlan || []).reduce((s, t) => s + (parseFloat(t.price) || 0) * (parseInt(t.sessions) || 1), 0))}</span>
              </div>
              <div className="quote-row"><span>Skincare subtotal</span>
                <span>{fmt((data.skincareRecs || []).reduce((s, p) => s + (parseFloat(p.rrp) || 0) * (parseInt(p.qty) || 1), 0))}</span>
              </div>
              <div className="quote-row quote-subtotal"><span>Subtotal</span><span>{fmt(data.quoteSubtotal)}</span></div>
              <div className="quote-row">
                <span>Discount %</span>
                <input type="number" min="0" max="100" step="0.5"
                  value={data.quoteDiscount}
                  onChange={e => setF({ quoteDiscount: e.target.value })}
                  style={{ width: '70px', textAlign: 'right' }}
                />
              </div>
              <div className="quote-row quote-total"><span>Total</span><span>{fmt(data.quoteTotal)}</span></div>
            </div>
            <div className="form-field" style={{ marginTop: '1rem' }}>
              <label>Quote Notes</label>
              <textarea rows={3} value={data.quoteNotes} onChange={e => setF({ quoteNotes: e.target.value })} placeholder="Payment plan, validity period, special terms..." />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button className="btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
          <button className="btn-gold" onClick={onSave}><Save size={14} /> Save Consultation</button>
        </div>
      </div>
    );
  };

  const filtered = consultations.filter(c =>
    `${c.clientName} ${c.date}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Consultations</h2>
          <p className="section-sub">{consultations.length} consultation{consultations.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-gold" onClick={() => { setShowNew(true); setExpanded(null); setActiveTab('plan'); }}>
          <Plus size={14} /> New Consultation
        </button>
      </div>

      <div className="search-bar">
        <Search size={15} className="search-icon" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by client or date..." className="search-input" />
      </div>

      {showNew && (
        <div className="patient-card expanded" style={{ marginBottom: '1rem' }}>
          <div className="card-header">
            <div className="patient-name-block">
              <div className="patient-initials">+</div>
              <div><div className="patient-name">New Consultation</div></div>
            </div>
          </div>
          <ConsultationForm data={form} setData={setForm} onSave={saveNew} onCancel={() => { setShowNew(false); setForm(emptyConsultation); }} isNew />
        </div>
      )}

      <div className="patient-list">
        {filtered.map(con => (
          <div key={con.id} className={`patient-card ${expanded === con.id ? 'expanded' : ''}`}>
            <div className="card-header" onClick={() => { setExpanded(expanded === con.id ? null : con.id); setEditing(null); setActiveTab('plan'); }}>
              <div className="patient-name-block">
                <div className="patient-initials">{con.clientName?.[0]}{con.clientName?.split(' ')?.[1]?.[0]}</div>
                <div>
                  <div className="patient-name">{con.clientName}</div>
                  <div className="patient-meta">
                    <span>{con.date ? new Date(con.date).toLocaleDateString('en-GB') : ''}</span>
                    {(con.treatmentPlan || []).length > 0 && <span>{con.treatmentPlan.length} treatment{con.treatmentPlan.length !== 1 ? 's' : ''}</span>}
                    {(con.skincareRecs || []).length > 0 && <span>{con.skincareRecs.length} product{con.skincareRecs.length !== 1 ? 's' : ''}</span>}
                    {con.quoteTotal > 0 && <span className="mini-tag">£{parseFloat(con.quoteTotal).toFixed(2)}</span>}
                  </div>
                </div>
              </div>
              <div className="card-actions" onClick={e => e.stopPropagation()}>
                <button className="icon-btn" title="Print treatment plan" onClick={() => printConsultation(con, 'plan')}><Printer size={14} /></button>
                <button className="icon-btn" title="Print skincare recommendations" onClick={() => printConsultation(con, 'skincare')}><Package size={14} /></button>
                <button className="icon-btn" title="Email supplier order" onClick={() => emailOrder(con)}><Mail size={14} /></button>
                <button className="icon-btn" onClick={() => { setEditing(con); setExpanded(con.id); setActiveTab('plan'); }}><Edit3 size={14} /></button>
                <button className="icon-btn danger" onClick={() => del(con.id)}><Trash2 size={14} /></button>
                <span className="expand-icon">{expanded === con.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
              </div>
            </div>

            {expanded === con.id && (
              <div className="card-body">
                {editing?.id === con.id ? (
                  <ConsultationForm data={editing} setData={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
                ) : (
                  <>
                    <div className="patient-tabs">
                      {[
                        { id: 'plan', label: 'Treatment Plan', icon: ClipboardList },
                        { id: 'skincare', label: 'Skincare', icon: Package },
                        { id: 'supplements', label: 'Supplements', icon: Leaf },
                        { id: 'quote', label: 'Quote', icon: FileText },
                      ].map(tab => (
                        <button key={tab.id} className={`patient-tab ${activeTab === tab.id ? 'active' : ''}`}
                          onClick={() => setActiveTab(tab.id)}>
                          <tab.icon size={12} /> {tab.label}
                        </button>
                      ))}
                    </div>

                    {activeTab === 'plan' && (
                      <div className="detail-grid">
                        {con.consultationNotes && <div className="detail-row full"><span className="detail-label">Notes</span><span>{con.consultationNotes}</span></div>}
                        {con.skinAssessment && <div className="detail-row full"><span className="detail-label">Skin Assessment</span><span>{con.skinAssessment}</span></div>}
                        {(con.treatmentPlan || []).length > 0 && (
                          <div className="detail-row full">
                            <span className="detail-label">Treatment Plan</span>
                            <div style={{ marginTop: '6px' }}>
                              {con.treatmentPlan.map((t, i) => (
                                <div key={i} className="view-rec-row">
                                  <span>{t.name}</span>
                                  <span className="soft">{t.sessions} session{t.sessions !== 1 ? 's' : ''}</span>
                                  <span>£{t.price}</span>
                                  {t.notes && <span className="soft">{t.notes}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="form-actions" style={{ paddingTop: '0.5rem' }}>
                          <button className="btn-ghost" onClick={() => printConsultation(con, 'plan')}><Printer size={14} /> Print Plan</button>
                        </div>
                      </div>
                    )}

                    {activeTab === 'skincare' && (
                      <div className="detail-grid">
                        {(con.skincareRecs || []).length === 0 ? <p className="log-empty">No skincare recommendations.</p> : (
                          <div className="detail-row full">
                            {con.skincareRecs.map((p, i) => (
                              <div key={i} className="view-rec-row" style={{ flexWrap: 'wrap' }}>
                                <strong>{p.brand} {p.name}</strong>
                                {p.whenToUse && <span className="mini-tag">{p.whenToUse}</span>}
                                <span>£{p.rrp}</span>
                                {p.notes && <span className="soft" style={{ width: '100%', paddingLeft: '0' }}>{p.notes}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="form-actions" style={{ paddingTop: '0.5rem' }}>
                          <button className="btn-ghost" onClick={() => printConsultation(con, 'skincare')}><Printer size={14} /> Print Recs</button>
                          <button className="btn-ghost" onClick={() => emailOrder(con)}><Mail size={14} /> Email Order</button>
                        </div>
                      </div>
                    )}

                    {activeTab === 'supplements' && (
                      <div className="detail-grid">
                        {(con.supplementRecs || []).length === 0 ? <p className="log-empty">No supplement recommendations.</p> : (
                          con.supplementRecs.map((s, i) => (
                            <div key={i} className="view-rec-row">
                              <strong>{s.name}</strong>
                              {s.dosage && <span className="soft">{s.dosage}</span>}
                              {s.notes && <span className="soft">{s.notes}</span>}
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {activeTab === 'quote' && (
                      <div className="detail-grid">
                        {con.quoteSubtotal > 0 ? (
                          <div className="detail-row full">
                            <div className="quote-summary view-only">
                              <div className="quote-row"><span>Subtotal</span><span>{fmt(con.quoteSubtotal)}</span></div>
                              {parseFloat(con.quoteDiscount) > 0 && <div className="quote-row"><span>Discount ({con.quoteDiscount}%)</span><span>-{fmt(con.quoteSubtotal * con.quoteDiscount / 100)}</span></div>}
                              <div className="quote-row quote-total"><span>Total</span><span>{fmt(con.quoteTotal)}</span></div>
                            </div>
                            {con.quoteNotes && <p className="soft" style={{ marginTop: '8px', fontSize: '12px' }}>{con.quoteNotes}</p>}
                          </div>
                        ) : <p className="log-empty">No quote generated.</p>}
                        {con.quoteTotal > 0 && (
                          <div className="form-actions" style={{ paddingTop: '0.5rem' }}>
                            <button className="btn-ghost" onClick={() => printConsultation(con, 'all')}><Printer size={14} /> Print Full Consultation</button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && !showNew && (
          <div className="empty-state">
            <p>No consultations yet</p>
            <button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> New Consultation</button>
          </div>
        )}
      </div>
    </div>
  );
}
